import React, { useCallback, useState } from 'react'
import TaskItemsListPanel from '../../DetailDashboard/components/TaskItemsListPanel'
import TasksListPanel from '../../DetailDashboard/components/TasksListPanel'
import TaskBudgetsListPanel from '../../DetailDashboard/components/TaskBudgetsListPanel'
import TaskPerformancesListPanel from '../../DetailDashboard/components/TaskPerformancesListPanel'
import TaskDetailPanel from '@/pages/project/TaskManagement/components/TaskDetailPanel'
import TaskCreatePanel from '@/pages/project/TaskManagement/components/TaskCreatePanel'
import TaskItemCreatePanel from '@/pages/project/TaskManagement/components/TaskItemCreatePanel'
import type { TaskTreeNode } from '@/pages/project/TaskManagement/components/TaskHierarchyTable'
import ReportDetailPanel from '@/pages/project/ReportManagement/components/ReportDetailPanel'
import { useTaskEditFromReport } from '@/hooks/useTaskEditFromReport'
import type { ReportTemplateDetailResponse } from '@/api/generated/models/ReportTemplateDetailResponse'
import { TaskReportService } from '@/api'

/** 요약 카드가 가리키는 항목 종류 */
export type SummaryListPanelType = 'task_item' | 'task' | 'budget' | 'performance'

export interface SummaryListPanelState {
  type: SummaryListPanelType
  title: string
  /** 목록을 특정 부서로 좁힐 때 (업무 진행 현황 섹션) */
  departmentId?: string
  departmentName?: string
  /** 목록을 특정 사업으로 좁힐 때 (사업별 업무 진행 현황 탭) */
  businessId?: string
  businessName?: string
  /** 조회 범위 (스냅샷 시점은 X-As-Of 헤더로 별도 전달) */
  startDate?: Date | null
  endDate?: Date | null
}

interface SummaryListPanelsProps {
  /** 열린 패널 정보 (null이면 모두 닫힘) */
  panel: SummaryListPanelState | null
  onClose: () => void
  workspaceId?: string
}

/**
 * 요약 카드 클릭 시 뜨는 항목별 목록 사이드패널 + 그 안의 상세/보고서 패널 묶음.
 * 누적 현황(OverallDashboard)의 동작을 그대로 재사용한다. 기간은 X-As-Of 헤더가 결정.
 */
const SummaryListPanels: React.FC<SummaryListPanelsProps> = ({
  panel,
  onClose,
  workspaceId,
}) => {
  // 목록 패널 행 클릭 → 항목 상세 패널 (z-index가 목록 패널보다 위)
  const [isTaskDetailPanelOpen, setIsTaskDetailPanelOpen] = useState(false)
  const [selectedTaskIdForDetail, setSelectedTaskIdForDetail] = useState<string | null>(null)
  const [isTaskItemDetailPanelOpen, setIsTaskItemDetailPanelOpen] = useState(false)
  const [selectedTaskItemIdForDetail, setSelectedTaskItemIdForDetail] = useState<string | null>(null)
  const [isBudgetDetailPanelOpen, setIsBudgetDetailPanelOpen] = useState(false)
  const [selectedBudgetTaskIdForDetail, setSelectedBudgetTaskIdForDetail] = useState<string | null>(null)
  const [isPerformanceDetailPanelOpen, setIsPerformanceDetailPanelOpen] = useState(false)
  const [selectedPerformanceTaskIdForDetail, setSelectedPerformanceTaskIdForDetail] = useState<string | null>(null)

  const handleTaskRowClick = useCallback((task: TaskTreeNode) => {
    if (!task?.id) return
    setSelectedTaskIdForDetail(task.id)
    setIsTaskDetailPanelOpen(true)
  }, [])
  const handleTaskItemRowClick = useCallback((taskItem: TaskTreeNode) => {
    if (!taskItem?.id) return
    setSelectedTaskItemIdForDetail(taskItem.id)
    setIsTaskItemDetailPanelOpen(true)
  }, [])
  const handleBudgetItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedBudgetTaskIdForDetail(taskId)
    setIsBudgetDetailPanelOpen(true)
  }, [])
  const handlePerformanceItemClick = useCallback((taskId: string) => {
    if (!taskId) return
    setSelectedPerformanceTaskIdForDetail(taskId)
    setIsPerformanceDetailPanelOpen(true)
  }, [])

  // ===== 상세 패널 "수정" 클릭 → 수정 사이드패널 (OverallDashboard와 동일 패턴) =====
  const [isTaskEditPanelOpen, setIsTaskEditPanelOpen] = useState(false)
  const [editTaskData, setEditTaskData] = useState<{
    id: string
    name: string
    taskSubgroupId: string
    taskSubgroupName: string
    taskGroupName: string
    businessName: string
    businessId: string
    assigneeId: string
    assigneeName: string
    subAssigneeId: string
    subAssigneeName: string
    managerId: string
    managerName: string
    reviewerId: string
    reviewerName: string
    departmentId: string
    departmentName: string
    startAt: string
    endAt: string
    description: string
    labels?: Array<{ id: string; business_label_id: string; key: string }>
    detailInfos?: Array<{ id: string; business_detail_field_id: string; key: string; name: string; value: string }>
    extraInfos?: Array<{ id: string; business_extra_field_id: string; key: string; name: string; value: string }>
    /** AI 분석 프롬프트에 함께 실리는 판단근거 */
    judgmentBasis?: string
    /** 편집 패널의 budget/performance 초기값에 사용 (raw originalData) */
    taskOriginal?: any
  } | null>(null)
  const [isTaskItemEditPanelOpen, setIsTaskItemEditPanelOpen] = useState(false)
  const [editTaskItemData, setEditTaskItemData] = useState<{
    id: string
    name: string
    taskId: string
    taskName: string
    taskSubgroupName: string
    businessName: string
    startAt: string
    endAt: string
    output: string
  } | null>(null)
  // 수정 성공 시 증가 → 상세 패널이 자체 재조회
  const [taskDetailRefreshKey, setTaskDetailRefreshKey] = useState(0)
  // 수정 성공 시 증가 → 세부실행과제 목록 패널 재조회
  // 수정/삭제 후 열려 있는 목록 패널을 다시 조회하기 위한 트리거 (네 종류 공용)
  const [listRefreshTrigger, setListRefreshTrigger] = useState(0)

  const handleTaskEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task' || !task.originalData) return
    const taskData = task.originalData as any
    setEditTaskData({
      id: task.id,
      name: task.name,
      taskSubgroupId: taskData.task_subgroup_id || '',
      taskSubgroupName: taskData.task_subgroup_name || '',
      taskGroupName: taskData.task_group_name || '',
      businessName: task.business_name || '',
      businessId: taskData.business_id || '',
      assigneeId: taskData.assignee?.id || '',
      assigneeName: taskData.assignee?.name || '',
      subAssigneeId: taskData.sub_assignee?.id || '',
      subAssigneeName: taskData.sub_assignee?.name || '',
      managerId: taskData.manager?.id || '',
      managerName: taskData.manager?.name || '',
      reviewerId: taskData.reviewer?.id || '',
      reviewerName: taskData.reviewer?.name || '',
      departmentId: taskData.department_id || '',
      departmentName: task.department_name || '',
      startAt: taskData.start_at || '',
      endAt: taskData.end_at || '',
      description: taskData.description || '',
      labels: taskData.labels || [],
      detailInfos: taskData.detail_infos || [],
      extraInfos: taskData.extra_infos || [],
      judgmentBasis: taskData.judgment_basis || '',
      taskOriginal: taskData
    })
    setIsTaskEditPanelOpen(true)
    // 상세 패널은 열린 상태 유지 — 수정 패널이 위(z)에 뜬다
  }, [])

  const handleTaskItemEdit = useCallback((task: TaskTreeNode) => {
    if (task.type !== 'task_item' || !task.originalData) return
    const taskItemData = task.originalData as any
    setEditTaskItemData({
      id: task.id,
      name: task.name,
      taskId: taskItemData.task_id || '',
      taskName: taskItemData.task_name || '',
      taskSubgroupName: taskItemData.task_subgroup_name || '',
      businessName: task.business_name || '',
      startAt: taskItemData.start_at || '',
      endAt: taskItemData.end_at || '',
      output: taskItemData.output || ''
    })
    setIsTaskItemEditPanelOpen(true)
  }, [])

  const handleTaskEditPanelClose = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
  }, [])

  const handleTaskEditSuccess = useCallback(() => {
    setIsTaskEditPanelOpen(false)
    setEditTaskData(null)
    // 상세 패널 재조회 + 열려 있는 세부실행과제 목록 패널 재조회
    setTaskDetailRefreshKey(k => k + 1)
    setListRefreshTrigger(t => t + 1)
  }, [])

  const handleTaskItemEditPanelClose = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
  }, [])

  const handleTaskItemEditSuccess = useCallback(() => {
    setIsTaskItemEditPanelOpen(false)
    setEditTaskItemData(null)
    setTaskDetailRefreshKey(k => k + 1)
    setListRefreshTrigger(t => t + 1)
  }, [])

  // 보고서(액션 버튼) 클릭 시 행 클릭이 함께 발화하는 것 차단용 플래그
  const [isActionButtonClicked, setIsActionButtonClicked] = useState(false)

  // 보고서 상세 패널
  const [reportPanelState, setReportPanelState] = useState({
    isOpen: false,
    selectedTask: null as TaskTreeNode | null,
    selectedTemplate: null as { key: string; name: string } | null,
  })
  const [reportTemplates, setReportTemplates] = useState<ReportTemplateDetailResponse[]>([])
  const [isReportTemplatesLoading, setIsReportTemplatesLoading] = useState(false)
  const [reportTemplatesError, setReportTemplatesError] = useState<string | null>(null)

  const fetchReportTemplates = useCallback(async (wsId: string, businessId?: string | null) => {
    if (!wsId) return null
    setIsReportTemplatesLoading(true)
    setReportTemplatesError(null)
    try {
      const response = await TaskReportService.appsTaskViewsGetReportTemplatesByWorkspace({
        workspaceId: wsId,
        businessId: businessId || undefined,
      })
      setReportTemplates(response || [])
      return response || []
    } catch (error) {
      console.error('Failed to fetch report templates:', error)
      setReportTemplatesError('보고서 템플릿 목록을 불러오는데 실패했습니다.')
      return null
    } finally {
      setIsReportTemplatesLoading(false)
    }
  }, [])

  const handleReportClick = useCallback(async (task: TaskTreeNode) => {
    setIsActionButtonClicked(true)
    setTimeout(() => setIsActionButtonClicked(false), 100)

    setIsTaskDetailPanelOpen(false)
    setSelectedTaskIdForDetail(null)

    if (workspaceId) {
      const businessId = (task.originalData as any)?.business_id || (task as any).business_id
      const templates = await fetchReportTemplates(workspaceId, businessId)
      if (templates && templates.length > 0) {
        const firstTemplate = templates[0]
        setReportPanelState({
          isOpen: true,
          selectedTask: task,
          selectedTemplate: { key: firstTemplate.key, name: firstTemplate.name },
        })
      } else {
        setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
      }
    } else {
      setReportPanelState({ isOpen: true, selectedTask: task, selectedTemplate: null })
    }
  }, [workspaceId, fetchReportTemplates])

  const handleReportDetailPanelClose = useCallback(() => {
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
  }, [])

  // 보고서 패널의 "세부실행과제 수정" → 보고서 패널을 닫고 수정 패널을 연다. 저장 후 목록 재조회
  const handleReportTaskEditSuccess = useCallback(() => {
    setListRefreshTrigger(t => t + 1)
  }, [])
  const { openTaskEdit: handleReportTaskEdit, taskEditPanel: reportTaskEditPanel } = useTaskEditFromReport({
    onBeforeOpen: handleReportDetailPanelClose,
    onSuccess: handleReportTaskEditSuccess,
  })

  const handleReportTemplateChange = useCallback((templateKey: string, templateName: string) => {
    setReportPanelState(prev => ({
      ...prev,
      selectedTemplate: templateKey ? { key: templateKey, name: templateName } : null,
    }))
  }, [])

  const handleReportTaskDetail = useCallback(() => {
    const selectedTask = reportPanelState.selectedTask
    if (!selectedTask?.id) return
    setReportPanelState({ isOpen: false, selectedTask: null, selectedTemplate: null })
    setSelectedTaskIdForDetail(selectedTask.id)
    setIsTaskDetailPanelOpen(true)
  }, [reportPanelState.selectedTask])

  const commonProps = {
    onClose,
    workspaceId: workspaceId || '',
    selectedDepartmentId: panel?.departmentId,
    selectedDepartmentName: panel?.departmentName,
    selectedBusinessId: panel?.businessId,
    selectedBusinessName: panel?.businessName,
    startDate: panel?.startDate ?? null,
    endDate: panel?.endDate ?? null,
  }

  return (
    <>
      {/* 항목별 목록 사이드패널 (한 번에 하나만 열림) */}
      <TaskItemsListPanel
        refreshTrigger={listRefreshTrigger}
        {...commonProps}
        isOpen={panel?.type === 'task_item'}
        title={panel?.type === 'task_item' ? panel.title : '수행업무 목록'}
        onTaskItemClick={handleTaskItemRowClick}
      />
      <TasksListPanel
        {...commonProps}
        isOpen={panel?.type === 'task'}
        title={panel?.type === 'task' ? panel.title : '세부실행과제 목록'}
        onTaskClick={handleTaskRowClick}
        onReportClick={handleReportClick}
        isActionButtonClicked={isActionButtonClicked}
        refreshTrigger={listRefreshTrigger}
      />
      <TaskBudgetsListPanel
        refreshTrigger={listRefreshTrigger}
        {...commonProps}
        isOpen={panel?.type === 'budget'}
        title={panel?.type === 'budget' ? panel.title : '예산 목록'}
        onBudgetItemClick={handleBudgetItemClick}
      />
      <TaskPerformancesListPanel
        refreshTrigger={listRefreshTrigger}
        {...commonProps}
        isOpen={panel?.type === 'performance'}
        title={panel?.type === 'performance' ? panel.title : '성과 목록'}
        onPerformanceItemClick={handlePerformanceItemClick}
      />

      {/* 목록 패널 행 클릭 → 항목 상세 패널 */}
      <TaskDetailPanel
        isOpen={isTaskDetailPanelOpen}
        onClose={() => { setIsTaskDetailPanelOpen(false); setSelectedTaskIdForDetail(null) }}
        taskId={selectedTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isTaskItemDetailPanelOpen}
        onClose={() => { setIsTaskItemDetailPanelOpen(false); setSelectedTaskItemIdForDetail(null) }}
        taskId={selectedTaskItemIdForDetail}
        taskType="task_item"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskItemEdit}
      />
      <TaskDetailPanel
        isOpen={isBudgetDetailPanelOpen}
        onClose={() => { setIsBudgetDetailPanelOpen(false); setSelectedBudgetTaskIdForDetail(null) }}
        taskId={selectedBudgetTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />
      <TaskDetailPanel
        isOpen={isPerformanceDetailPanelOpen}
        onClose={() => { setIsPerformanceDetailPanelOpen(false); setSelectedPerformanceTaskIdForDetail(null) }}
        taskId={selectedPerformanceTaskIdForDetail}
        taskType="task"
        refreshKey={taskDetailRefreshKey}
        onEdit={handleTaskEdit}
      />

      {/* 보고서 상세 패널 */}
      <ReportDetailPanel
        isOpen={reportPanelState.isOpen}
        onClose={handleReportDetailPanelClose}
        taskId={reportPanelState.selectedTask?.id ?? null}
        reportTemplateKey={reportPanelState.selectedTemplate?.key ?? null}
        reportTemplates={reportTemplates}
        isReportTemplatesLoading={isReportTemplatesLoading}
        reportTemplatesError={reportTemplatesError}
        workspaceId={workspaceId || ''}
        onReportTypeChange={handleReportTemplateChange}
        onTaskClick={handleReportTaskDetail}
        onEditTask={handleReportTaskEdit}
      />

      {/* 세부실행과제 수정 패널 — 보고서 패널의 "세부실행과제 수정" 클릭 시 */}
      {reportTaskEditPanel}

      {/* 수행업무 수정 패널 — 상세 패널의 "수정" 클릭 시 (상세 패널은 열린 상태 유지) */}
      {editTaskItemData && (
        <TaskItemCreatePanel
          isOpen={isTaskItemEditPanelOpen}
          onClose={handleTaskItemEditPanelClose}
          taskId={editTaskItemData.taskId}
          taskName={editTaskItemData.taskName}
          taskSubgroupName={editTaskItemData.taskSubgroupName}
          businessName={editTaskItemData.businessName}
          onSuccess={handleTaskItemEditSuccess}
          editMode={true}
          taskItemId={editTaskItemData.id}
          initialTaskItemData={{
            name: editTaskItemData.name,
            startAt: editTaskItemData.startAt,
            endAt: editTaskItemData.endAt,
            output: editTaskItemData.output
          }}
        />
      )}

      {/* 세부실행과제 수정 패널 — budget/performance 초기값은 taskOriginal에서 추출 */}
      {editTaskData && (() => {
        const taskOriginal = editTaskData.taskOriginal
        return (
          <TaskCreatePanel
            isOpen={isTaskEditPanelOpen}
            onClose={handleTaskEditPanelClose}
            taskSubgroupId={editTaskData.taskSubgroupId}
            taskSubgroupName={editTaskData.taskSubgroupName}
            taskGroupName={editTaskData.taskGroupName}
            businessName={editTaskData.businessName}
            businessId={editTaskData.businessId}
            onSuccess={handleTaskEditSuccess}
            editMode={true}
            taskId={editTaskData.id}
            initialTaskData={{
              name: editTaskData.name,
              assigneeId: editTaskData.assigneeId,
              assigneeName: editTaskData.assigneeName,
              subAssigneeId: editTaskData.subAssigneeId,
              subAssigneeName: editTaskData.subAssigneeName,
              managerId: editTaskData.managerId,
              managerName: editTaskData.managerName,
              reviewerId: editTaskData.reviewerId,
              reviewerName: editTaskData.reviewerName,
              departmentId: editTaskData.departmentId,
              departmentName: editTaskData.departmentName,
              startAt: editTaskData.startAt,
              endAt: editTaskData.endAt,
              description: editTaskData.description,
              budget: (taskOriginal as any)?.budget ? {
                id: (taskOriginal as any).budget.id || '',
                resultDescription: (taskOriginal as any).budget.result_description || '',
                items: ((taskOriginal as any).budget.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              performance: (taskOriginal as any)?.performance ? {
                id: (taskOriginal as any).performance.id || '',
                name: (taskOriginal as any).performance.name || '',
                unit: (taskOriginal as any).performance.unit || '',
                items: ((taskOriginal as any).performance.items || []).map((item: any) => ({
                  id: item.id || '',
                  name: item.name || '',
                  targetFigure: item.target_figure || 0,
                  doneFigure: item.done_figure || 0
                }))
              } : undefined,
              labels: editTaskData.labels,
              detailInfos: editTaskData.detailInfos,
              extraInfos: editTaskData.extraInfos,
              judgmentBasis: editTaskData.judgmentBasis
            }}
          />
        )
      })()}
    </>
  )
}

export default SummaryListPanels
